import React from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Skeleton } from 'primereact/skeleton';
import './LoadingComponents.css';

// Centered spinner for full page/section loading
export const LoadingSpinner: React.FC<{ message?: string }> = ({ message }) => {
  return (
    <div className="loading-spinner-container">
      <ProgressSpinner 
        style={{ width: '50px', height: '50px' }} 
        strokeWidth="4" 
        animationDuration=".8s" 
      />
      {message && <p className="loading-message">{message}</p>}
    </div>
  );
};

// Inline spinner for buttons and small areas
export const InlineSpinner: React.FC<{ size?: string }> = ({ size = '20px' }) => {
  return (
    <ProgressSpinner 
      style={{ width: size, height: size }} 
      strokeWidth="4" 
      animationDuration=".8s" 
    />
  );
};

// Skeleton loader for tables
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 4 
}) => {
  return (
    <div className="table-skeleton">
      {/* Header */}
      <div className="skeleton-row skeleton-header">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={`header-${i}`} width="100%" height="2rem" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={`row-${rowIndex}`} className="skeleton-row">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={`cell-${rowIndex}-${colIndex}`} width="100%" height="1.5rem" />
          ))}
        </div>
      ))}
    </div>
  );
};

// Skeleton loader for cards
export const CardSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => {
  return (
    <div className="card-skeleton-container">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card-skeleton">
          <Skeleton width="100%" height="150px" className="mb-3" />
          <Skeleton width="70%" height="1.5rem" className="mb-2" />
          <Skeleton width="100%" height="1rem" className="mb-2" />
          <Skeleton width="100%" height="1rem" className="mb-2" />
          <Skeleton width="50%" height="1rem" />
        </div>
      ))}
    </div>
  );
};

// Skeleton loader for dashboard stats
export const StatsSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => {
  return (
    <div className="stats-skeleton-container">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="stat-skeleton">
          <div className="stat-skeleton-icon">
            <Skeleton shape="circle" size="3rem" />
          </div>
          <div className="stat-skeleton-content">
            <Skeleton width="60%" height="1rem" className="mb-2" />
            <Skeleton width="80%" height="2rem" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Skeleton loader for lists
export const ListSkeleton: React.FC<{ items?: number }> = ({ items = 5 }) => {
  return (
    <div className="list-skeleton">
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="list-skeleton-item">
          <Skeleton shape="circle" size="2.5rem" className="mr-3" />
          <div className="list-skeleton-content">
            <Skeleton width="60%" height="1.2rem" className="mb-2" />
            <Skeleton width="90%" height="0.9rem" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Form skeleton loader
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 4 }) => {
  return (
    <div className="form-skeleton">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="form-field-skeleton">
          <Skeleton width="30%" height="1rem" className="mb-2" />
          <Skeleton width="100%" height="2.5rem" />
        </div>
      ))}
      <div className="form-actions-skeleton">
        <Skeleton width="100px" height="2.5rem" className="mr-2" />
        <Skeleton width="100px" height="2.5rem" />
      </div>
    </div>
  );
};

// Chart skeleton loader
export const ChartSkeleton: React.FC<{ height?: string }> = ({ height = '300px' }) => {
  return (
    <div className="chart-skeleton" style={{ height }}>
      <Skeleton width="100%" height="100%" />
    </div>
  );
};

// Loading overlay for existing content
export const LoadingOverlay: React.FC<{ loading: boolean; children: React.ReactNode }> = ({ 
  loading, 
  children 
}) => {
  return (
    <div className="loading-overlay-container">
      {children}
      {loading && (
        <div className="loading-overlay">
          <ProgressSpinner 
            style={{ width: '50px', height: '50px' }} 
            strokeWidth="4" 
            animationDuration=".8s" 
          />
        </div>
      )}
    </div>
  );
};